/**
 * src/components/shared/StuckLoadingWatch.jsx — TICKET-1104.
 *
 * WHY: on iOS a crashed WebKit network process kills every request the CURRENT
 * page makes — fetches reject 'Load failed', IndexedDB throws 'internal error' —
 * and nothing inside that page can revive it (TICKET-1100 measured the retries
 * never leaving the phone). Only a fresh page load does. So a view gated on the
 * app-wide `loading` flag (Projects: "Loading…") sat there until force-quit
 * (Agustina's iPhone, 2026-10-05, every launch since 08:44Z).
 *
 * One watch for every tab, mounted once in AppShell:
 *   - BANNER_MS stuck → banner with a hard-reload button;
 *   - AUTO_RELOAD_MS stuck → ONE automatic hard reload, guarded per GUARD_MS so a
 *     device that is genuinely offline never reload-loops;
 *   - before any reload, a breadcrumb in localStorage that the NEXT boot reports
 *     (reportStuckReloadBreadcrumb) — the dead page cannot send telemetry itself.
 */
import { useEffect, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { useAppDataContext } from '../../lib/AppDataContext';
import { useT } from '../../lib/i18n/I18nContext';
import { logAppError } from '../../lib/errorReporter';

export const BANNER_MS = 8000;
export const AUTO_RELOAD_MS = 20000;
export const GUARD_MS = 5 * 60 * 1000;
export const GUARD_KEY = 'acn_stuck_autoreload_at';
export const BREADCRUMB_KEY = 'acn_stuck_reload_breadcrumb';

const safe = (fn, fallback = null) => { try { return fn(); } catch { return fallback; } };

function writeBreadcrumb(view, trigger, stuckMs) {
  safe(() => localStorage.setItem(BREADCRUMB_KEY, JSON.stringify({
    view, trigger, stuck_ms: stuckMs,
    last_idb_op: globalThis.__acnLastIdbOp || null,
    online: typeof navigator !== 'undefined' ? navigator.onLine : null,
    at: new Date().toISOString(),
  })));
}

/** Boot-time: report (once) a reload the previous, network-dead page forced. */
export function reportStuckReloadBreadcrumb() {
  const raw = safe(() => localStorage.getItem(BREADCRUMB_KEY));
  if (!raw) return;
  safe(() => localStorage.removeItem(BREADCRUMB_KEY));
  const crumb = safe(() => JSON.parse(raw));
  if (!crumb) return;
  logAppError({
    type: 'stuck_loading_recovered',
    message: `Hard reload after ${crumb.stuck_ms}ms stuck on ${crumb.view} (${crumb.trigger})`,
    metadata: { ...crumb, ua: typeof navigator !== 'undefined' ? navigator.userAgent : null },
  });
}

const BANNER_STYLE = {
  position: 'fixed', left: 12, right: 12, zIndex: 300,
  top: 'calc(env(safe-area-inset-top, 0px) + 8px)',
  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 12,
  background: 'var(--warn-tint)', color: 'var(--warn)', fontSize: 13, fontWeight: 600,
  boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
};
const BTN_STYLE = {
  display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 44, padding: '8px 12px',
  border: 'none', borderRadius: 8, cursor: 'pointer', flexShrink: 0,
  background: 'var(--warn-tint)', color: 'var(--warn)', outline: '1px solid var(--warn)', fontSize: 13, fontWeight: 700,
};

// Module-scope default: an inline default arrow is a NEW function every render, and
// in the effect deps it reset both timers on every render (Night Watch 2026-10-06).
const hardReload = () => window.location.reload();

// `force`: mounted inside a Suspense fallback (a lazy view chunk still downloading).
// A lazy import on a dead network never settles and has no timeout of its own.
export default function StuckLoadingWatch({ view = 'unknown', force = false, reloadPage = hardReload }) {
  const ctx = useAppDataContext();
  const loading = force || Boolean(ctx?.loading);
  const { t } = useT();
  const [stuck, setStuck] = useState(false);
  const startRef = useRef(0);
  // Latest reloadPage via ref, NOT an effect dep: a caller's inline arrow must not re-arm the timers.
  const reloadRef = useRef(reloadPage);
  reloadRef.current = reloadPage;

  useEffect(() => {
    setStuck(false);
    if (!loading) return undefined;
    startRef.current = Date.now();
    const banner = setTimeout(() => setStuck(true), BANNER_MS);
    const auto = setTimeout(() => {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return; // offline: a reload revives nothing
      const last = Number(safe(() => localStorage.getItem(GUARD_KEY), 0)) || 0;
      if (Date.now() - last < GUARD_MS) return; // already tried this session — never loop
      // TICKET-1105: localStorage, not sessionStorage — on the broken iPhone the
      // sessionStorage guard did not persist and two auto reloads fired 27s apart.
      safe(() => localStorage.setItem(GUARD_KEY, String(Date.now())));
      writeBreadcrumb(view, 'auto', Date.now() - startRef.current);
      reloadRef.current();
    }, AUTO_RELOAD_MS);
    return () => { clearTimeout(banner); clearTimeout(auto); };
  }, [loading, view]);

  if (!loading || !stuck) return null;
  return (
    <div data-testid="stuck-loading-banner" role="alert" style={BANNER_STYLE}>
      <span style={{ flex: 1, minWidth: 0 }}>{t('stuckLoading.message')}</span>
      <button
        type="button"
        data-testid="stuck-loading-reload"
        onClick={() => { writeBreadcrumb(view, 'button', Date.now() - startRef.current); reloadRef.current(); }}
        style={BTN_STYLE}
      >
        <RefreshCw size={14} /> {t('stuckLoading.reload')}
      </button>
    </div>
  );
}
